import { getPathologiesForSessionCall } from '@cannect/services/resources/instantConsultation/requests'
import { TPathology } from '@cannect/services/resources/instantConsultation/types'
import { Loader2, Stethoscope } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import { useBrand } from 'hooks/useBrand'
import { useInstantConsultation } from 'hooks/useInstantConsultation'
import { useTokenApi } from 'hooks/useTokenApi'

const MAX_PATHOLOGIES = 3

export function TriageStep() {
  const { selectedPathologies, setSelectedPathologies, goTo, handleCreateSession, loading } = useInstantConsultation()
  const { tokenApi } = useTokenApi()
  const { isBranded, homePath } = useBrand()
  const history = useHistory()
  const location = useLocation()

  const [pathologies, setPathologies] = useState<TPathology[]>([])
  const [loadingPathologies, setLoadingPathologies] = useState(false)

  useEffect(() => {
    const fetchPathologies = async () => {
      setLoadingPathologies(true)
      try {
        const list = await getPathologiesForSessionCall()
        setPathologies(list)
      } catch {
        toast.error('Erro ao carregar condições de saúde.')
      } finally {
        setLoadingPathologies(false)
      }
    }
    fetchPathologies()
  }, [])

  const togglePathology = (pathology: TPathology) => {
    const isSelected = selectedPathologies.some((p) => p.id === pathology.id)
    if (isSelected) {
      setSelectedPathologies(selectedPathologies.filter((p) => p.id !== pathology.id))
    } else {
      if (selectedPathologies.length >= MAX_PATHOLOGIES) {
        toast.error('Você só pode selecionar até 3 patologias.')
        return
      }
      setSelectedPathologies([...selectedPathologies, pathology])
    }
  }

  const isSelected = (id: number) => selectedPathologies.some((p) => p.id === id)

  const handleContinue = async () => {
    if (selectedPathologies.length === 0) {
      toast.error('Selecione pelo menos uma patologia')
      return
    }

    if (!tokenApi) {
      // Whitelabel não tem login: volta ao início do funil da marca, onde o
      // paciente se identifica (o /login da Cannect quebraria a marca).
      if (isBranded) {
        history.push(homePath)
        return
      }
      // Mantém o pathname atual para voltar à versão branded (/vikura/...)
      history.push({
        pathname: '/login',
        state: { from: { pathname: location.pathname } }
      })
      return
    }

    try {
      await handleCreateSession()
      goTo('questionnaire')
    } catch {
      // toast já exibido em handleCreateSession
    }
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <div className="mb-8 flex flex-col items-center gap-3 text-center">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }}>
          <Stethoscope className="h-8 w-8 text-primary-700" />
        </div>
        <h1 className="font-spartan text-2xl font-bold text-gray-800 md:text-3xl">
          Consulta <span className="text-primary-700">na hora</span>
        </h1>
        <p className="max-w-md text-sm text-gray-500 md:text-base">
          Conecte-se agora com um especialista em cannabis medicinal. Selecione de 1 a 3 condições que deseja tratar
          para que o médico se prepare para a consulta.
        </p>
      </div>

      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-gray-700">
          Quais condições você deseja tratar? <span className="text-red-500">*</span>
        </h2>
        <p className="mb-4 text-xs text-gray-400">Mínimo 1 e máximo {MAX_PATHOLOGIES} opções.</p>

        {loadingPathologies ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary-700" />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {pathologies.map((pathology) => (
              <button
                key={pathology.id}
                type="button"
                onClick={() => togglePathology(pathology)}
                className={`rounded-full border border-solid px-4 py-2 text-sm font-medium transition-all ${
                  isSelected(pathology.id)
                    ? 'border-primary-700 bg-primary-700 text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                }`}>
                {pathology.name}
              </button>
            ))}
          </div>
        )}

        {selectedPathologies.length > 0 && (
          <p className="mt-4 text-sm text-primary-700">
            {selectedPathologies.length} condição{selectedPathologies.length !== 1 ? 'ões' : ''} selecionada
            {selectedPathologies.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => {
            handleContinue()
          }}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg border-none bg-primary-700 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-primary-800 disabled:opacity-60">
          {loading && <Loader2 className="h-5 w-5 animate-spin" />}
          Continuar
        </button>
      </div>
    </div>
  )
}
